// src/components/settings/catalog/CatalogImportHistory.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 6 (screen): IMPORT HISTORY.
//
// Every finished (or unfinished) import, newest first, from listCatalogImports (import job records
// are closed to the app, so the server lists them). Each row: date, who, file, counts, status
// (Imported / Undone / Partly undone / Not finished / Undo not finished).
//   "Undo this import" → a plain confirm → startCatalogImportUndo → the job runner (progress, then a
//   summary of items put back, items left alone because they were edited since, and items archived
//   because the import added them). One undo per import; the server refuses a second.
//   "Finish it" carries on an import or an undo that stopped part-way (nothing is done twice).
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Undo2 } from 'lucide-react';
import { importHistoryStatus, importCountsLine } from '../../../utils/catalogUi';
import { listCatalogImports, startCatalogImportUndo, friendlyError } from './catalogApi';
import { BTN_GHOST, BTN_PRIMARY, BTN_LINK, Dialog, ErrorLine } from './catalogUiParts';

const fmt = (n) => (n || 0).toLocaleString('en-US');
const when = (ms) => (typeof ms === 'number' ? new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const TONE = {
    Imported: 'bg-emerald-50 text-emerald-800',
    Undone: 'bg-gray-100 text-gray-700',
    'Partly undone': 'bg-amber-50 text-amber-800',
    'Not finished': 'bg-orange-50 text-orange-800',
    'Undo not finished': 'bg-orange-50 text-orange-800',
};

export default function CatalogImportHistory({ runner, reloadSignal, onBack, onNewImport }) {
    const [list, setList] = useState(null); // null = loading
    const [cursor, setCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState(null); // the import being undone
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState('');
    const running = !!runner.state;

    const load = useCallback(async () => {
        setError('');
        setList(null);
        try {
            const r = await listCatalogImports();
            setList(r.imports || []);
            setCursor(r.nextCursor || null);
        } catch (err) {
            console.error('Catalog: import history failed', err);
            setError(friendlyError(err, "Couldn't load the import history. Try again."));
            setList([]);
        }
    }, []);
    useEffect(() => { load(); }, [load, reloadSignal]);

    const more = async () => {
        setLoadingMore(true);
        try {
            const r = await listCatalogImports(cursor);
            setList((l) => [...(l || []), ...(r.imports || [])]);
            setCursor(r.nextCursor || null);
        } catch (err) {
            setError(friendlyError(err, "Couldn't load more. Try again."));
        } finally {
            setLoadingMore(false);
        }
    };

    const finishImport = (e) => runner.run({ jobId: e.jobId, action: 'import', total: e.total, processed: 0, newCount: (e.summary && e.summary.new) || 0, fileName: e.fileName });

    const startUndo = async (e) => {
        setBusy(true);
        setActionError('');
        try {
            const u = await startCatalogImportUndo(e.jobId);
            setConfirm(null);
            runner.run({ jobId: u.jobId, action: 'importUndo', total: u.total, processed: 0, importJobId: e.jobId, fileName: e.fileName });
        } catch (err) {
            console.error('Catalog: undo refused', err);
            setActionError(friendlyError(err, "Couldn't start the undo. Try again."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-5 max-w-[1100px]">
            <button type="button" className={BTN_LINK} onClick={onBack} disabled={running}>← Back to Catalog</button>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-[22px] sm:text-[26px] font-bold text-gray-900">Import history</h2>
                    <p className="text-sm text-gray-500 mt-1">Every import, newest first. Undo puts the items an import changed back the way they were.</p>
                </div>
                <button type="button" className={BTN_PRIMARY} onClick={onNewImport} disabled={running}>Import a price list</button>
            </div>

            <ErrorLine text={error} onRetry={load} />
            <ErrorLine text={confirm ? '' : actionError} />

            {list === null && (
                <p className="text-sm text-gray-600 flex items-center gap-2" aria-live="polite"><Loader2 size={16} className="animate-spin text-[#5F4EC9]" /> Loading…</p>
            )}
            {list && !list.length && !error && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center space-y-2">
                    <p className="text-base font-semibold text-gray-800">No imports yet</p>
                    <p className="text-sm text-gray-600">When you import a price list, it shows up here so you can undo it if you need to.</p>
                </div>
            )}
            {list && list.length > 0 && (
                <ul className="space-y-2">
                    {list.map((e) => {
                        const status = importHistoryStatus(e);
                        const undoUnfinished = status === 'Undo not finished';
                        return (
                            <li key={e.jobId} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 space-y-0.5">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-gray-900 break-all">{e.fileName || 'import.csv'}</span>
                                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${TONE[status]}`}>{status}</span>
                                    </div>
                                    <div className="text-sm text-gray-600">{when(e.createdAtMs)}{e.createdBy && e.createdBy.name ? ` · ${e.createdBy.name}` : ''}</div>
                                    <div className="text-sm text-gray-700">{importCountsLine(e)}</div>
                                    {e.undo && e.undo.status === 'done' && (
                                        <div className="text-xs text-gray-500">
                                            Undone {when(e.undo.finishedAtMs || e.undo.createdAtMs)}{e.undo.by && e.undo.by.name ? ` by ${e.undo.by.name}` : ''}
                                            {e.undo.counts && e.undo.counts.skipped > 0 ? ` · ${fmt(e.undo.counts.skipped)} edited since, left as they are` : ''}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {e.status !== 'done' && <button type="button" className={BTN_PRIMARY} disabled={running} onClick={() => finishImport(e)}>Finish it</button>}
                                    {undoUnfinished && <button type="button" className={BTN_PRIMARY} disabled={running || busy} onClick={() => startUndo(e)}>Finish the undo</button>}
                                    {e.canUndo && !undoUnfinished && (
                                        <button type="button" className={BTN_GHOST} disabled={running} onClick={() => { setActionError(''); setConfirm(e); }}><Undo2 size={16} /> Undo this import</button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            {cursor && list && (
                <button type="button" className={BTN_GHOST} disabled={loadingMore} onClick={more}>
                    {loadingMore && <Loader2 size={16} className="animate-spin" />} Show older imports
                </button>
            )}

            <Dialog open={!!confirm} title="Undo this import?" onClose={() => setConfirm(null)} busy={busy}
                footer={confirm && (
                    <>
                        <button type="button" className={BTN_GHOST} disabled={busy} onClick={() => setConfirm(null)}>Keep it</button>
                        <button type="button" className={BTN_PRIMARY} disabled={busy} onClick={() => startUndo(confirm)}>
                            {busy && <Loader2 size={16} className="animate-spin" />} Undo this import
                        </button>
                    </>
                )}>
                {confirm && (
                    <div className="space-y-2 text-sm text-gray-700">
                        <p>This puts back every item that <strong>{confirm.fileName || 'this import'}</strong> changed, exactly as it was before ({importCountsLine(confirm)}).</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Items edited since the import are left as they are. You'll see a list of them.</li>
                            <li>Items the import added are archived, not deleted.</li>
                            <li>Estimates and invoices already written don't change.</li>
                        </ul>
                        <p>You can only undo an import once.</p>
                        <ErrorLine text={actionError} />
                    </div>
                )}
            </Dialog>
        </div>
    );
}
